import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { type MixPanelEventMap, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import type { PortfolioLabContextProps, PortfolioLabInvestmentType } from "./ctx";

/**
 * The session-mutating interactions surfaced to analytics. Kept as the single source of truth via the
 * event map so this union and the `portfolioLab.session.change` payload can never drift apart.
 */
export type PortfolioLabSessionChangeTrigger = MixPanelEventMap["portfolioLab.session.change"]["trigger"];

/**
 * Tracks a portfolio-lab session change together with a snapshot of the session. Call it AFTER the
 * `investments$` write has been applied so the snapshot reflects the post-change session (the atom
 * writes are synchronous/optimistic, so a read right after the write already sees the new state).
 */
export function trackPortfolioLabSessionChange(
	ctx: PortfolioLabContextProps,
	trigger: PortfolioLabSessionChangeTrigger,
): void {
	const investments = readAtom(ctx.investments$);
	const investmentsByType: Record<PortfolioLabInvestmentType, number> = {
		INVESTMENT: 0,
		INVESTMENT_PROPOSAL: 0,
		BENCHMARK: 0,
		REFERENCE: 0,
	};
	for (const investment of investments) {
		investmentsByType[investment.type]++;
	}
	trackMixPanelEvent("portfolioLab.session.change", {
		trigger,
		columns: investments.length,
		investmentsByType,
		investments: investments.map((investment) => ({
			virtualUuid: investment.virtualUuid,
			uuid: investment.uuid,
			type: investment.type,
		})),
		visibleCharts: readAtom(ctx.charts$).filter((chart) => chart.visible).length,
	});
}
